import { useEffect, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Trash2, Edit, Plus, X, User } from "lucide-react";

const ROLES = ["Batsman", "Bowler", "All-Rounder", "Wicket Keeper"];

const empty = { name: "", role: "", tehsil: "", photo_file_id: "" };

export default function AdminSelectedPlayers() {
  const [players, setPlayers] = useState([]);
  const [tehsils, setTehsils] = useState([]);
  const [filterTehsil, setFilterTehsil] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const params = filterTehsil ? { tehsil: filterTehsil } : {};
    const r = await api.get("/selected-players", { params });
    setPlayers(r.data);
  };
  useEffect(() => {
    api.get("/meta").then((r) => setTehsils(r.data.tehsils || []));
  }, []);
  useEffect(() => { load(); }, [filterTehsil]);

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file); fd.append("folder", "selected_players");
    try {
      const r = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, photo_file_id: r.data.file_id }));
      toast.success("Photo uploaded");
    } catch { toast.error("Upload failed"); }
    setUploading(false);
  };

  const openNew = () => { setForm(empty); setEditingId(null); setShowForm(true); };
  const openEdit = (p) => { setForm({ name: p.name, role: p.role, tehsil: p.tehsil, photo_file_id: p.photo_file_id || "" }); setEditingId(p.id); setShowForm(true); };
  const closeForm = () => { setShowForm(false); setForm(empty); setEditingId(null); };

  const save = async () => {
    if (!form.name || !form.role || !form.tehsil) { toast.error("Name, Role, and Tehsil are required"); return; }
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/admin/selected-players/${editingId}`, form);
        toast.success("Player updated");
      } else {
        await api.post("/admin/selected-players", form);
        toast.success("Player added");
      }
      closeForm(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setSaving(false);
  };

  const del = async (id) => {
    if (!confirm("Delete this selected player?")) return;
    try { await api.delete(`/admin/selected-players/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.4em] text-[#39FF14]">Official Squad</div>
          <h1 className="font-display text-4xl mt-2">Selected Players</h1>
        </div>
        <button onClick={openNew} data-testid="sp-add-btn" className="px-5 py-2 bg-[#39FF14] text-black font-bold uppercase tracking-widest text-xs flex items-center gap-2"><Plus size={14}/>Add Player</button>
      </div>

      <div className="mt-6 flex items-center gap-3 flex-wrap">
        <div className="text-xs uppercase tracking-widest text-white/60">Filter by tehsil</div>
        <select value={filterTehsil} onChange={(e) => setFilterTehsil(e.target.value)} className="bg-black/50 border border-white/15 px-3 py-2 text-sm" data-testid="sp-filter-tehsil">
          <option value="" className="bg-[#0B132B]">All Tehsils</option>
          {tehsils.map((t) => <option key={t} value={t} className="bg-[#0B132B]">{t}</option>)}
        </select>
        <div className="text-xs text-white/50">{players.length} player{players.length !== 1 ? "s" : ""}</div>
      </div>

      <div className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="sp-list">
        {players.length === 0 && <div className="col-span-full text-white/40 text-sm">No selected players yet.</div>}
        {players.map((p) => (
          <div key={p.id} className="relative bg-[#0B132B] border border-white/10 rounded-lg overflow-hidden group">
            <div className="aspect-[3/4] bg-black/40 relative overflow-hidden">
              {p.photo_file_id ? <img src={fileUrl(p.photo_file_id)} alt={p.name} className="w-full h-full object-cover"/> : <div className="w-full h-full grid place-items-center text-white/30"><User size={48}/></div>}
            </div>
            <div className="p-4">
              <div className="font-display text-xl">{p.name}</div>
              <div className="text-[#FFD700] text-xs uppercase tracking-widest">{p.role}</div>
              <div className="text-xs text-white/50 mt-1">{p.tehsil}</div>
            </div>
            <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <button onClick={() => openEdit(p)} data-testid={`sp-edit-${p.id}`} className="p-2 bg-black/70 hover:bg-[#FFD700] hover:text-black"><Edit size={14}/></button>
              <button onClick={() => del(p.id)} data-testid={`sp-del-${p.id}`} className="p-2 bg-black/70 hover:bg-[#FF2442] hover:text-white text-[#FF2442]"><Trash2 size={14}/></button>
            </div>
          </div>
        ))}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/80 z-50 grid place-items-center p-4" onClick={closeForm}>
          <div className="bg-[#0B132B] border border-white/15 max-w-md w-full p-6 rounded-lg" onClick={(e) => e.stopPropagation()} data-testid="sp-form">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-2xl">{editingId ? "Edit" : "Add"} Selected Player</h2>
              <button onClick={closeForm} className="text-white/60"><X /></button>
            </div>
            <div className="mt-5 space-y-4">
              <div>
                <div className="text-xs uppercase tracking-widest text-white/60 mb-2">Photo</div>
                <div className="flex items-center gap-3">
                  {form.photo_file_id ? <img src={fileUrl(form.photo_file_id)} alt="preview" className="w-20 h-20 object-cover rounded border border-white/10"/> : <div className="w-20 h-20 grid place-items-center border border-dashed border-white/15 text-white/30"><User size={24}/></div>}
                  <label className="flex-1 flex items-center gap-2 border border-dashed border-white/15 px-3 py-3 cursor-pointer text-xs hover:border-[#39FF14]">
                    <Upload size={14} className="text-[#39FF14]"/>
                    <span>{uploading ? "Uploading…" : form.photo_file_id ? "Replace photo" : "Choose photo"}</span>
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadPhoto(e.target.files?.[0])} data-testid="sp-photo-input"/>
                  </label>
                </div>
              </div>
              <label className="block">
                <span className="text-xs uppercase tracking-widest text-white/60">Player Name</span>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 w-full bg-black/50 border border-white/15 focus:border-[#39FF14] focus:outline-none px-3 py-2 text-sm" data-testid="sp-name"/>
              </label>
              <label className="block">
                <span className="text-xs uppercase tracking-widest text-white/60">Playing Role</span>
                <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="mt-2 w-full bg-black/50 border border-white/15 px-3 py-2 text-sm" data-testid="sp-role">
                  <option value="" className="bg-[#0B132B]">Select role</option>
                  {ROLES.map((r) => <option key={r} value={r} className="bg-[#0B132B]">{r}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="text-xs uppercase tracking-widest text-white/60">Tehsil</span>
                <select value={form.tehsil} onChange={(e) => setForm({ ...form, tehsil: e.target.value })} className="mt-2 w-full bg-black/50 border border-white/15 px-3 py-2 text-sm" data-testid="sp-tehsil">
                  <option value="" className="bg-[#0B132B]">Select tehsil</option>
                  {tehsils.map((t) => <option key={t} value={t} className="bg-[#0B132B]">{t}</option>)}
                </select>
              </label>
            </div>
            <div className="mt-6 flex gap-3">
              <button onClick={save} disabled={saving} data-testid="sp-save" className="flex-1 py-3 bg-[#39FF14] text-black font-bold uppercase tracking-widest text-xs disabled:opacity-50">{saving ? "Saving…" : editingId ? "Update Player" : "Add Player"}</button>
              <button onClick={closeForm} className="px-5 py-3 border border-white/20 text-xs uppercase tracking-widest">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
